import React from 'react';
import AccountantSidebar from './components/AccountantSidebar';
import Header from '../teacher_screen/components/Header';
import {
    BarChart3,
    PieChart,
    TrendingUp,
    Download,
    Calendar,
    ArrowUpRight,
    ArrowDownRight,
    Search,
    ChevronDown,
    FileText,
    CreditCard,
    DollarSign,
    Filter
} from 'lucide-react';

export default function AccountantReports() {
    const reportCategories = [
        { title: 'Fee Collection Analysis', desc: 'Detailed breakdown of monthly and term-wise collections.', icon: DollarSign, color: 'text-blue-500 bg-blue-50' },
        { title: 'Defaulter Report', desc: 'List of students with overdue fees exceeding 30 days.', icon: FileText, color: 'text-rose-500 bg-rose-50' },
        { title: 'Income Statement', desc: 'Overview of all institutional revenue and operational expenses.', icon: TrendingUp, color: 'text-emerald-500 bg-emerald-50' },
        { title: 'Scholarship Audit', desc: 'Track students under fee waiver or scholarship programs.', icon: CreditCard, color: 'text-amber-500 bg-amber-50' },
    ];

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <AccountantSidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title="Financial Reports"
                    subtitle="Advanced data analytics and institutional fiscal reports generation"
                >
                    <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[14px] font-bold text-gray-700 shadow-sm hover:bg-gray-50 uppercase tracking-tight">
                        <Calendar size={18} className="text-blue-500" />
                        March 2026
                        <ChevronDown size={14} className="ml-1 opacity-40" />
                    </button>
                    <button className="flex items-center gap-3 py-2.5 px-6 bg-blue-500 text-white rounded-xl text-[14px] font-bold shadow-lg shadow-blue-100 hover:bg-blue-600 transition-all">
                        <Download size={18} />
                        Export All
                    </button>
                </Header>

                <div className="max-w-7xl mx-auto pb-10">
                    {/* Key Metrics Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
                        <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm flex flex-col justify-between min-h-[200px] transition-all hover:scale-[1.02] cursor-default group">
                            <div className="flex items-center justify-between mb-8">
                                <div className="p-3 bg-blue-50 text-blue-500 rounded-2xl group-hover:bg-blue-500 group-hover:text-white transition-all"><TrendingUp size={24} /></div>
                                <span className="text-[11px] font-black text-emerald-500 bg-emerald-50 px-3 py-1.5 rounded-full uppercase tracking-widest">+12.4% MoM</span>
                            </div>
                            <div>
                                <h3 className="text-4xl font-black text-[#111827] mb-2 tracking-tighter">₹1.2Cr</h3>
                                <p className="text-gray-400 font-bold text-xs uppercase tracking-widest leading-none">Projected Revenue</p>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm flex flex-col justify-between min-h-[200px] transition-all hover:scale-[1.02] cursor-default group">
                            <div className="flex items-center justify-between mb-8">
                                <div className="p-3 bg-amber-50 text-amber-500 rounded-2xl group-hover:bg-amber-600 group-hover:text-white transition-all"><PieChart size={24} /></div>
                                <span className="text-[11px] font-black text-amber-500 bg-amber-50 px-3 py-1.5 rounded-full uppercase tracking-widest">88% Target met</span>
                            </div>
                            <div>
                                <h3 className="text-4xl font-black text-[#111827] mb-2 tracking-tighter">₹84.2L</h3>
                                <p className="text-gray-400 font-bold text-xs uppercase tracking-widest leading-none">Net Collections</p>
                            </div>
                        </div>

                        <div className="bg-[#111827] p-8 rounded-[32px] shadow-2xl shadow-blue-900/10 flex flex-col justify-between min-h-[200px] text-white relative overflow-hidden group hover:scale-[1.02] transition-all">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 opacity-20 blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                            <div className="flex items-center justify-between relative z-10">
                                <div className="p-3 bg-white/10 text-white rounded-2xl"><Download size={24} /></div>
                                <span className="text-[11px] font-black text-white/40 uppercase tracking-[0.2em] leading-none text-right">Instant<br />Audit Access</span>
                            </div>
                            <div className="relative z-10">
                                <h3 className="text-[20px] font-black uppercase tracking-tighter mb-2 leading-none">Audit Records</h3>
                                <button className="w-full mt-4 py-3 bg-blue-500 rounded-xl text-[12px] font-black uppercase tracking-widest hover:bg-white hover:text-blue-500 transition-all">Download Log</button>
                            </div>
                        </div>
                    </div>

                    {/* Report Types */}
                    <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden flex flex-col mb-10">
                        <div className="p-8 border-b border-gray-50 flex items-center justify-between">
                            <h3 className="text-[20px] font-black text-[#111827]">Financial Catalog</h3>
                            <div className="flex items-center gap-4">
                                <div className="relative group">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                                    <input type="text" placeholder="Search reports..." className="h-11 pl-11 pr-4 bg-white border border-gray-100 rounded-xl text-[14px] font-bold text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all w-64" />
                                </div>
                                <button className="p-2.5 bg-gray-50 text-gray-400 rounded-xl hover:bg-blue-50 hover:text-blue-500 transition-all">
                                    <Filter size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {reportCategories.map((cat, idx) => (
                                <div key={idx} className="bg-[#F8FAFC] p-8 rounded-[24px] border border-transparent hover:border-blue-100 hover:bg-white transition-all cursor-pointer group flex flex-col min-h-[300px]">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shrink-0 transition-transform group-hover:scale-110 ${cat.color} group-hover:shadow-lg transition-all`}>
                                        <cat.icon size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-[17px] font-black text-[#111827] uppercase tracking-tighter leading-tight mb-3 group-hover:text-blue-500 transition-colors">{cat.title}</h3>
                                        <p className="text-[13px] font-bold text-gray-400 italic leading-snug">{cat.desc}</p>
                                    </div>
                                    <div className="mt-6 flex items-center gap-2 text-[11px] font-black text-blue-500 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0">
                                        Generate <ArrowUpRight size={14} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
